import * as React from 'react'
import styled from 'styled-components'
import { colors } from '../../styleConstants'
import { Delete } from 'styled-icons/material'
import { Dialog } from '../common/Dialog'
import { ReservationState, Modal } from './useReservationState'
import { Mode, useEditAttributeState } from './useEditAttributeState'
import { I18nCatalogue } from '../../i18n/reservation/types'
import { getPreferredLanguage } from '../../i18n'
import { Input } from '../common/Input'
import { Select } from '../common/Select'
import { Menu } from '../../types/menu'
import { HairStylist } from '../../types/hairStylist'
import { Button } from '../common/Button'
import { updateReservedSpot, deleteReservedSpot } from '../../types/reservation'
import moment from 'moment'
import DateTimePicker from '../../components/common/DateTimePicker/DateTimePicker'

interface Props {
  readonly catalogue: I18nCatalogue
  readonly pageState: ReservationState
  readonly stylist: HairStylist
  readonly special_spots: { value: string; label: string }[]
  readonly menus: Menu[]
  readonly onEdited?: () => void
}

interface State {
  readonly selectedDateTime: string
  readonly selectedSpotNumber: string
  readonly selectedSpecialSpotId: string
}

export function EditReservation({ catalogue, pageState, stylist, special_spots, menus, onEdited }: Props) {
  const notificationId = `edit-reservation-` + pageState.selectedTime
  const inputReservationReleaseDateRef = React.useRef<HTMLInputElement>(null)
  const { actionState, onClose, onAction, onChange } = useEditAttributeState(pageState, notificationId, '')
  const action = onAction(callUpdateReservation, catalogue.reservationEdit)
  const spotOption = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
  ]

  const daysAgo = moment(pageState.selectedTime).diff(moment(pageState.reservedSpot!.releaseTime), 'days')

  const selectedDateTime = pageState.selectedTime
  const selectedSpotNumber = pageState.reservedSpot!.spots.toString()
  const reserveCount = pageState.reservedSpot!.reserves.length
  const selectedSpecialSpotId = pageState.reservedSpot!.specialSpot ? pageState.reservedSpot!.specialSpot.id : ''
  const [state, setState] = React.useState<State>({ selectedDateTime, selectedSpotNumber, selectedSpecialSpotId })
  const initialDate = moment(pageState.selectedTime).toDate()
  const dateTimePickerLocale = getPreferredLanguage() == 'ja' ? 'ja' : 'en'

  async function callUpdateReservation() {
    const spotId = pageState.reservedSpot!.id.toString()

    let releaseDate = ''
    let daysAgo = -1

    if (inputReservationReleaseDateRef.current != null) {
      daysAgo =
        inputReservationReleaseDateRef.current.value != '' ? inputReservationReleaseDateRef.current.valueAsNumber : -1
    }

    if (state.selectedSpecialSpotId) {
      if (!menus.some(menu => menu.special_spot)) {
        alert(catalogue.regist.noHasMenu)
        throw new Error('Failed to Reservation Spot')
      }
    } else {
      // 通常メニュー登録時にメニューが存在していない場合はエラーとする
      // そのため、メニュー一覧から特定枠でないメニューが存在しているかをチェック
      if (!menus.some(menu => !menu.special_spot)) {
        alert(catalogue.regist.noHasMenu)
        throw new Error('Failed to Reservation Spot')
      }
    }

    releaseDate =
      daysAgo != -1
        ? moment(pageState.selectedTime, 'YYYY/MM/DD')
            .subtract({ days: daysAgo })
            .format('YYYY/MM/DD HH:mm:ss')
        : moment().format('YYYY/MM/DD HH:mm:ss')

    const [isSuccess, reponseStatus] = await updateReservedSpot(
      spotId,
      state.selectedDateTime,
      state.selectedSpotNumber,
      releaseDate,
      state.selectedSpecialSpotId,
    )
    if (!isSuccess) {
      if (reponseStatus === 422) {
        alert(catalogue.regist.spotError)
      } else {
        alert(catalogue.reservationEdit.failed)
      }
      throw new Error('Failed to Reservation Spot')
    } else {
      if (onEdited) {
        onEdited()
      }
    }

    pageState.setState({
      modal: Modal.None,
      selectedTime: '',
      reservedSpot: null,
      cancelReservation: null,
      spots: [],
      isMobile: false,
    })
  }

  async function calldeleteReservation() {
    const spotId = pageState.reservedSpot!.id.toString()

    const isSuccess = await deleteReservedSpot(spotId)
    if (!isSuccess) {
      alert(catalogue.reservationEdit.deleteFailed)
    } else {
      if (onEdited) {
        onEdited()
      }
    }
  }

  function setSelectedSpotNumber(event: React.ChangeEvent<HTMLSelectElement>) {
    setState({
      selectedDateTime: state.selectedDateTime,
      selectedSpotNumber: event.target.value,
      selectedSpecialSpotId: state.selectedSpecialSpotId,
    })
  }

  function getSalonNameById(salonId: string) {
    let salonName = ''
    stylist.salons.map(salon => {
      if (salon.id.toString() == salonId) {
        salonName = salon.name
      }
    })

    return salonName
  }

  async function clickedDeleteButton() {
    if (pageState.reservedSpot!.reserves.length > 0) {
      alert(catalogue.reservationEdit.canNotDelete)
      return
    }

    if (!confirm(catalogue.reservationEdit.confirmDelete)) {
      return
    }

    calldeleteReservation()
    pageState.setState({
      modal: Modal.None,
      selectedTime: '',
      reservedSpot: null,
      cancelReservation: null,
      spots: [],
      isMobile: false,
    })
  }

  function changedDateTimePicker(date: any) {
    const changedDateTime = moment(date).format('YYYY/MM/DD HH:mm')
    setState({
      selectedDateTime: changedDateTime,
      selectedSpotNumber: state.selectedSpotNumber,
      selectedSpecialSpotId: state.selectedSpecialSpotId,
    })
  }

  function setSelectedSpecialSpots(event: React.ChangeEvent<HTMLSelectElement>) {
    setState({
      selectedDateTime: state.selectedDateTime,
      selectedSpotNumber: state.selectedSpotNumber,
      selectedSpecialSpotId: event.target.value,
    })
  }

  return (
    <Dialog
      onClose={onClose}
      title={catalogue.reservationEdit.title}
      data-test="Edit-reservation-of-modal"
      actions={[
        {
          label: catalogue.end,
          look: 'primary',
          onClick: action,
        },
      ]}>
      {actionState.mode === Mode.Failed && <ErrorMessage>{actionState.message}</ErrorMessage>}
      <DialogItem>
        <span style={{ float: 'left', marginRight: '18px' }}>{catalogue.reservationDateTime}:</span>
        {reserveCount == 0 && (
          <DateTimePicker
            locale={dateTimePickerLocale}
            defaultDateTime={initialDate}
            onChanged={changedDateTimePicker}
          />
        )}
        {reserveCount > 0 && <span>{pageState.selectedTime}</span>}
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.salon}:</span>
        <span>{getSalonNameById(pageState.reservedSpot!.salonId)}</span>
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.reservablePeople}:</span>
        <Select
          options={spotOption}
          onChange={event => setSelectedSpotNumber(event)}
          defaultValue={state.selectedSpotNumber}
        />
      </DialogItem>
      <DialogItem>
        <span style={{ float: 'left', paddingTop: '5px' }}>{catalogue.reservationReleaseDate}:</span>
        <Input
          type="number"
          fluid
          onChange={onChange}
          ref={inputReservationReleaseDateRef}
          defaultValue={daysAgo}
          style={{ width: 60, float: 'left', verticalAlign: 'middle', margin: '0 12px' }}
        />
        <span style={{ verticalAlign: '-webkit-baseline-middle' }}>{catalogue.daysAgo}</span>
      </DialogItem>
      {stylist.specialSpotFlag && (
        <DialogItem>
          <span style={{ marginRight: '18px' }}>{catalogue.specialSpot}:</span>
          <Select
            options={special_spots}
            onChange={event => setSelectedSpecialSpots(event)}
            defaultValue={state.selectedSpecialSpotId}
          />
        </DialogItem>
      )}
      <DialogItem>
        <Button look={'primary'} onClick={clickedDeleteButton} style={{ marginTop: '60px' }}>
          <Delete size="24" />
        </Button>
      </DialogItem>
    </Dialog>
  )
}
const DialogItem = styled.div`
  margin: 18px;
  height: 36px;
`

const ErrorMessage = styled.div`
  font-weight: bold;
  color: ${colors.error};
`
